// AddRole.tsx (updated)
import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  Form,
  Row,
  Col,
  Card,
  Button,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import WebService from "../../utility/WebService";

const defaultPerm = () => ({
  canView: false,
  canAdd: false,
  canEdit: false,
  canDelete: false,
});

const AddRole = ({ show, handleClose, data }: any) => {
  const [roleName, setRoleName] = useState("");
  const [menus, setMenus] = useState<any[]>([]);
  const [permissionsMap, setPermissionsMap] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const roleId = data?._id || null;
  console.log("dataaaaaaaa", data);
  // new: expanded state for menus (track which menu ids are expanded)
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  // new: global check-all (controls all permissions globally)
  const [globalChecked, setGlobalChecked] = useState(false);

  // ---------------------------
  // Load Menus (for tree)
  // ---------------------------
  const loadMenus = async () => {
    try {
      setLoading(true);
      const res: any = await WebService.getAPI({ action: "role/left-menues" });
      const list = res?.data || [];

      setMenus(list);

      const map: any = {};
      const walk = (arr: any[]) => {
        arr.forEach((m) => {
          map[m._id] = { menuId: m._id, ...defaultPerm() };
          if (m.children?.length) walk(m.children);
        });
      };

      walk(list);
      setPermissionsMap(map);
      setGlobalChecked(computeGlobalChecked(map)); // ✅

      // init expandedMap collapsed by default
      const exp: Record<string, boolean> = {};
      const initExp = (arr: any[]) => {
        arr.forEach((m) => {
          exp[m._id] = false;
          if (m.children?.length) initExp(m.children);
        });
      };
      initExp(list);
      setExpandedMap(exp);
    } catch {
      toast.error("Failed to load menus");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Load role in edit mode
  // ---------------------------
  const loadRoleDetails = async () => {
    if (!roleId) return;
    try {
      setLoading(true);
      const res: any = await WebService.getAPI({
        action: `role/details/${roleId}`,
      });
      const role = res.data;

      setRoleName(role.roleName || "");

      const map: any = {};
      (role.permissions || []).forEach((p: any) => {
        map[p.menuId] = {
          menuId: p.menuId,
          canView: !!p.canView,
          canAdd: !!p.canAdd,
          canEdit: !!p.canEdit,
          canDelete: !!p.canDelete,
        };
      });

      setPermissionsMap((prev: any) => {
        const merged = { ...prev, ...map };
        setGlobalChecked(computeGlobalChecked(merged)); // ✅
        
        return merged;
      });
    } catch {
      toast.error("Failed to load role");
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  if (!show) return;

  (async () => {
    await loadMenus();       
    if (roleId) {
      await loadRoleDetails();
    } else {
      setRoleName("");
      setGlobalChecked(false);
    }
  })();

  return () => {
    setExpandedMap({});
    setGlobalChecked(false);
  };
}, [show]);


  // ---------------------------
  // Toggle a single permission
  // ---------------------------
// Returns ancestor IDs (root → parent) for a given menu ID
const getAncestorIds = (targetId: string, arr: any[], path: string[] = []): string[] | null => {
  for (const m of arr) {
    if (m._id === targetId) return path;
    if (m.children?.length) {
      const found = getAncestorIds(targetId, m.children, [...path, m._id]);
      if (found !== null) return found;
    }
  }
  return null;
};

const togglePermWithChildren = (menu: any, key: string) => {
  setPermissionsMap((prev: any) => {
    const next = { ...prev };

    const collect: string[] = [];
    const walk = (m: any) => {
      collect.push(m._id);
      if (m.children?.length) m.children.forEach(walk);
    };
    walk(menu);

    // Determine the toggled value from the clicked menu itself
    const clickedCurrent = prev[menu._id] || { menuId: menu._id, ...defaultPerm() };
    const turningOn = !clickedCurrent[key];

    collect.forEach((mid) => {
      const current = prev[mid] || { menuId: mid, ...defaultPerm() };
      const newValue = !current[key];

      let updated = { ...current, [key]: newValue };

      // If Add/Edit/Delete ON → View ON
      if (key !== "canView" && newValue === true) {
        updated.canView = true;
      }

      // If View OFF → Add/Edit/Delete OFF
      if (key === "canView" && newValue === false) {
        updated.canAdd = false;
        updated.canEdit = false;
        updated.canDelete = false;
      }

      next[mid] = updated;
    });

    // 👉 If turning ON any permission → set canView on all ancestor menus
    if (turningOn) {
      const ancestorIds = getAncestorIds(menu._id, menus) || [];
      ancestorIds.forEach((aid) => {
        const ancestor = next[aid] || { menuId: aid, ...defaultPerm() };
        next[aid] = { ...ancestor, canView: true };
      });
    }

    setGlobalChecked(computeGlobalChecked(next));
    return next;
  });
};



  // ---------------------------
  // Expand / Collapse handling
  // ---------------------------
  const toggleExpand = (menuId: string) => {
    setExpandedMap((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  const setAllForMenuAndChildren = (
    menu: any,
    permKey: string,
    value: boolean
  ) => {
    const collect: string[] = [];
    const walkCollect = (m: any) => {
      collect.push(m._id);
      if (m.children?.length) m.children.forEach(walkCollect);
    };
    walkCollect(menu);

    setPermissionsMap((prev: any) => {
      const next = { ...prev };
      collect.forEach((mid) => {
        next[mid] = {
          ...(next[mid] || { menuId: mid, ...defaultPerm() }),
          [permKey]: value,
        };
      });
      return next;
    });
  };

  // ---------------------------
  // Global check all toggle
  // ---------------------------
  const toggleGlobalAll = (checked: boolean) => {
    setGlobalChecked(checked);
    setPermissionsMap((prev: any) => {
      const next: any = {};
      Object.keys(prev).forEach((k) => {
        next[k] = {
          ...(prev[k] || { menuId: k, ...defaultPerm() }),
          canView: checked,
          canAdd: checked,
          canEdit: checked,
          canDelete: checked,
        };
      });
      return next;
    });
  };
  const computeGlobalChecked = (map: any) => {
    const values = Object.values(map);
    if (!values.length) return false;

    return values.every(
      (p: any) => p.canView && p.canAdd && p.canEdit && p.canDelete
    );
  };

  // ---------------------------
  // Render row
  // ---------------------------
  const renderMenuRow = (m: any, level = 0) => {
    const p = permissionsMap[m._id] || defaultPerm();
    const isExpanded = !!expandedMap[m._id];

    return (
      <div key={m._id} style={{ marginLeft: level * 15, padding: "6px 0" }}>
        <Row className="align-items-center border-bottom py-2">
          <Col xs={5}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: m.children?.length ? "pointer" : "default",
              }}
              onClick={() => {
                if (m.children?.length) toggleExpand(m._id);
              }}
            >
              {/* chevron */}
              {m.children?.length ? (
                <div
                  style={{
                    transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.15s",
                    width: 12,
                    textAlign: "center",
                    userSelect: "none",
                  }}
                >
                  ▶
                </div>
              ) : (
                <div style={{ width: 12 }} />
              )}

              <strong style={{ userSelect: "none" }}>{m.title}</strong>
            </div>

            {m.path && (
              <div>
                <small className="text-muted">{m.path}</small>
              </div>
            )}
          </Col>
          <Col xs={7} className="text-end">
            <Form.Check
              inline
              label="View"
              checked={p.canView}
              onChange={() => togglePermWithChildren(m, "canView")}
            />
            <Form.Check
              inline
              label="Add"
              checked={p.canAdd}
              onChange={() => togglePermWithChildren(m, "canAdd")}
            />
            <Form.Check
              inline
              label="Edit"
              checked={p.canEdit}
              onChange={() => togglePermWithChildren(m, "canEdit")}
            />
            <Form.Check
              inline
              label="Delete"
              checked={p.canDelete}
              onChange={() => togglePermWithChildren(m, "canDelete")}
            />
          </Col>
        </Row>

        {/* children -> only render if expanded */}
        {m.children?.length > 0 && isExpanded && (
          <div>
            {m.children.map((child: any) => renderMenuRow(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------
  // SAVE
  // ---------------------------
  const handleSave = async () => {
    if (!roleName.trim()) {
      toast.error("Role name is required");
      return;
    }

    const hasPermission = Object.values(permissionsMap).some(
      (p: any) => p.canView || p.canAdd || p.canEdit || p.canDelete
    );
    if (!hasPermission) {
      toast.error("Please select at least one permission");
      return;
    }

    setSaving(true);

    const payload = {
      roleName: roleName,
      permissions: Object.values(permissionsMap),
    };

    try {
      if (roleId) {
        await WebService.putAPI({
          action: `role/update/${roleId}`,
          body: payload,
        });
        toast.success("Role updated");
      } else {
        await WebService.postAPI({
          action: "role/create",
          body: payload,
        });
        toast.success("Role created");
      }

      handleClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        "Failed to save role";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      style={{ width: 1200 }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{roleId ? "Edit Role" : "Add Role"}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Card className="p-3 mb-3">
          <Form.Group>
            <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="Enter role name"
            />
          </Form.Group>

          <div className="d-flex align-items-center justify-content-between mt-2 mb-2">
            <div>
              <Form.Check
                type="checkbox"
                label="Check All"
                checked={globalChecked}
                onChange={(e) => toggleGlobalAll(e.target.checked)}
              />
            </div>
            <div>
              {/* quick-permission toggles for current menu tree */}
              <Button
                size="sm"
                variant="outline-secondary"
                className="me-2"
                onClick={() =>
                  setMenus((ms) => {
                    // toggle all top-level expanded
                    const nextExp = { ...expandedMap };
                    ms.forEach((m: any) => {
                      nextExp[m._id] = true;
                    });
                    setExpandedMap(nextExp);
                    return ms;
                  })
                }
              >
                Expand All
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() =>
                  setExpandedMap((prev) => {
                    const next: Record<string, boolean> = {};
                    Object.keys(prev).forEach((k) => (next[k] = false));
                    return next;
                  })
                }
              >
                Collapse All
              </Button>
            </div>
          </div>

          <div
            className="mt-3"
            style={{
              maxHeight: 600,
              overflowY: "auto",
              border: "1px solid #eee",
              borderRadius: 6,
            }}
          >
            {loading ? (
              <div className="p-4 text-center">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              menus.map((m) => renderMenuRow(m))
            )}
          </div>
        </Card>

        <div className="d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : roleId ? "Update Role" : "Create Role"}
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddRole;
